// Slice da feature inbox — só UI state. Dados de domínio ficam no cache do RTK Query.
// Cobre: conversation selecionada, visibilidade do painel direito, drafts por
// conversa, sugestões da IA por conversa, indicador de typing com TTL.
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { QuotedMessage } from './api';

export interface InboxUiState {
  selectedConversationId: string | null;
  customerPanelVisible: boolean;
  composerDrafts: Record<string, string>;
  aiSuggestions: Record<string, string | null>;
  /** Indicador transitório de "IA digitando"; o TTL é tocado pela listener middleware. */
  typingByConversation: Record<string, boolean>;
  /** RF-02: "IA aguardando mais mensagens" (janela de agrupamento aberta). TTL na listener. */
  batchingByConversation: Record<string, boolean>;
  // Filtro ativo na lista. RF-18: `AGUARDANDO` unifica QUEUE + ASSIGNED
  // ("Aguardando atendimento"), então não há mais chips QUEUE/ASSIGNED separados.
  // `EXPIRADAS` corta transversalmente os status: janela de 24h vencida (menos DONE).
  listFilter: 'ALL' | 'IA' | 'AGUARDANDO' | 'ACTIVE' | 'DONE' | 'EXPIRADAS';
  listSearch: string;
  /** Filtro por loja (multi-store): valor de storeId ou 'ALL'. */
  storeFilter: string | 'ALL';
  // Escopo da lista pra quem pode ver o time (>= GERENTE): 'mine' = só o
  // que EU assumi, 'team' = todos os atendentes das minhas lojas. Só de sessão, e
  // nasce em 'mine' de propósito: a Conversas é a caixa de trabalho de quem está
  // logado; a visão do time é uma escolha explícita. VENDEDOR ignora este estado
  // (o back recusa 'team' abaixo de GERENTE).
  // 🔴 'queue' entrou depois do primeiro onboarding: com o histórico recém-importado nada
  // está atribuído a ninguém, e a Conversas nascia VAZIA — parecendo que a importação não
  // trouxe nada. 'queue' mostra o que aguarda atendimento e não tem dono.
  scopeFilter: 'mine' | 'team' | 'queue';
  /** Modal "+ Nova conversa" aberto. */
  creatingConversation: boolean;
  // RF-28: conversas que o gestor destravou pra participar no modo observação.
  // Estado só de sessão (não persiste): ao clicar "Participar" numa conversa de
  // outro atendente, o composer destrava localmente. Ao enviar, o back registra
  // GESTOR_PARTICIPATED sozinho — o responsável (assignedToId) não muda.
  observationUnlocked: Record<string, boolean>;
  /**
   * Mensagem que o atendente está respondendo, por conversa (faixa "Respondendo a" do
   * composer). Guarda o recorte já no formato da citação: a bolha otimista nasce com ele.
   */
  replyingTo?: Record<string, QuotedMessage>;
}

const initialState: InboxUiState = {
  selectedConversationId: null,
  customerPanelVisible: true,
  composerDrafts: {},
  aiSuggestions: {},
  typingByConversation: {},
  batchingByConversation: {},
  listFilter: 'ALL',
  listSearch: '',
  storeFilter: 'ALL',
  scopeFilter: 'mine',
  creatingConversation: false,
  observationUnlocked: {},
  replyingTo: {},
};

const inboxSlice = createSlice({
  name: 'inboxUi',
  initialState,
  reducers: {
    conversationSelected(state, action: PayloadAction<{ id: string | null }>) {
      state.selectedConversationId = action.payload.id;
    },
    customerPanelToggled(state, action: PayloadAction<{ visible?: boolean } | undefined>) {
      const next = action?.payload?.visible;
      state.customerPanelVisible = next ?? !state.customerPanelVisible;
    },
    draftSet(state, action: PayloadAction<{ conversationId: string; value: string }>) {
      state.composerDrafts[action.payload.conversationId] = action.payload.value;
    },
    draftCleared(state, action: PayloadAction<{ conversationId: string }>) {
      delete state.composerDrafts[action.payload.conversationId];
    },
    aiSuggestionReceived(state, action: PayloadAction<{ conversationId: string; text: string }>) {
      state.aiSuggestions[action.payload.conversationId] = action.payload.text;
    },
    aiSuggestionConsumed(state, action: PayloadAction<{ conversationId: string }>) {
      state.aiSuggestions[action.payload.conversationId] = null;
    },
    typingReceived(state, action: PayloadAction<{ conversationId: string }>) {
      state.typingByConversation[action.payload.conversationId] = true;
    },
    typingExpired(state, action: PayloadAction<{ conversationId: string }>) {
      state.typingByConversation[action.payload.conversationId] = false;
    },
    // RF-02: janela de agrupamento aberta/fechada (indicador "aguardando mais mensagens").
    batchingReceived(state, action: PayloadAction<{ conversationId: string }>) {
      state.batchingByConversation[action.payload.conversationId] = true;
    },
    batchingCleared(state, action: PayloadAction<{ conversationId: string }>) {
      state.batchingByConversation[action.payload.conversationId] = false;
    },
    listFilterChanged(state, action: PayloadAction<{ filter: InboxUiState['listFilter'] }>) {
      state.listFilter = action.payload.filter;
    },
    listSearchChanged(state, action: PayloadAction<{ value: string }>) {
      state.listSearch = action.payload.value;
    },
    storeFilterChanged(state, action: PayloadAction<{ value: string | 'ALL' }>) {
      state.storeFilter = action.payload.value;
    },
    scopeFilterChanged(state, action: PayloadAction<{ value: InboxUiState['scopeFilter'] }>) {
      state.scopeFilter = action.payload.value;
    },
    creatingConversationToggled(state, action: PayloadAction<{ on: boolean }>) {
      state.creatingConversation = action.payload.on;
    },
    // RF-28: destrava o composer no modo observação pra a conversa indicada.
    observationUnlocked(state, action: PayloadAction<{ conversationId: string }>) {
      state.observationUnlocked[action.payload.conversationId] = true;
    },
    replyStarted(state, action: PayloadAction<{ conversationId: string; target: QuotedMessage }>) {
      (state.replyingTo ??= {})[action.payload.conversationId] = action.payload.target;
    },
    replyCleared(state, action: PayloadAction<{ conversationId: string }>) {
      if (state.replyingTo) delete state.replyingTo[action.payload.conversationId];
    },
  },
});

export const {
  conversationSelected,
  customerPanelToggled,
  draftSet,
  draftCleared,
  aiSuggestionReceived,
  aiSuggestionConsumed,
  typingReceived,
  typingExpired,
  batchingReceived,
  batchingCleared,
  listFilterChanged,
  listSearchChanged,
  storeFilterChanged,
  scopeFilterChanged,
  creatingConversationToggled,
  observationUnlocked,
  replyStarted,
  replyCleared,
} = inboxSlice.actions;

export const inboxUiReducer = inboxSlice.reducer;

// ---------- Selectors ----------
type Root = { inboxUi: InboxUiState };

export const selectInboxUi = (s: Root): InboxUiState => s.inboxUi;
export const selectSelectedConversationId = (s: Root): string | null =>
  s.inboxUi.selectedConversationId;
export const selectCustomerPanelVisible = (s: Root): boolean => s.inboxUi.customerPanelVisible;
export const selectDraftFor =
  (conversationId: string | null) =>
  (s: Root): string =>
    conversationId ? (s.inboxUi.composerDrafts[conversationId] ?? '') : '';
export const selectAiSuggestionFor =
  (conversationId: string | null) =>
  (s: Root): string | null =>
    conversationId ? (s.inboxUi.aiSuggestions[conversationId] ?? null) : null;
export const selectTypingFor =
  (conversationId: string | null) =>
  (s: Root): boolean =>
    conversationId ? (s.inboxUi.typingByConversation[conversationId] ?? false) : false;
export const selectBatchingFor =
  (conversationId: string | null) =>
  (s: Root): boolean =>
    conversationId ? (s.inboxUi.batchingByConversation[conversationId] ?? false) : false;
export const selectListFilter = (s: Root): InboxUiState['listFilter'] => s.inboxUi.listFilter;
export const selectListSearch = (s: Root): string => s.inboxUi.listSearch;
export const selectStoreFilter = (s: Root): string | 'ALL' => s.inboxUi.storeFilter;
export const selectScopeFilter = (s: Root): InboxUiState['scopeFilter'] => s.inboxUi.scopeFilter;
export const selectIsCreatingConversation = (s: Root): boolean => s.inboxUi.creatingConversation;
export const selectObservationUnlockedFor =
  (conversationId: string | null) =>
  (s: Root): boolean =>
    conversationId ? (s.inboxUi.observationUnlocked[conversationId] ?? false) : false;
export const selectReplyingTo =
  (conversationId: string | null) =>
  (s: Root): QuotedMessage | null =>
    conversationId ? (s.inboxUi.replyingTo?.[conversationId] ?? null) : null;
